import {
  normalizeHex,
  shapeSvg,
  type Color,
  type DesignAreaConfig,
  type DesignLayer,
  type DesignProblem,
  type DesignerViewSettings,
  type ResolvedDesignAreaConfig,
} from '@pz/scene';
import type {
  DesignerArtwork,
  DesignerFonts,
  DesignerRenderer,
  DesignerUploads,
} from '../designer-types.js';
import type { ToastKind } from '../layers.js';
import type { DesignerAction, DesignerStore } from './state.js';
import type { DesignerAllTexts } from './texts.js';

/**
 * The designer's chrome: the tool rail (a bottom bar on a phone), the tool
 * panel (a bottom sheet on a phone), and the small kit of controls every tool
 * panel is built from.
 *
 * Everything here is deliberately dumb — it draws what it is told and reports
 * taps. The rules live in state.ts, the pixels in canvas.ts, and each tool's
 * own panel is a function that fills a body element. That is what keeps six
 * tools from turning into one 3000-line file, and it is why the per-tool
 * modules import only this file and never each other.
 */

/**
 * Every panel the designer can open. The first block is Zazzle's rail
 * (Šabloni · Tekst · Slike · Pozadina · Elementi · Ikone · Slojevi, plus
 * Nalepnica where the product allows it); `alignment` and `effects` are the
 * two the contextual toolbar opens into the same slot; the rest are kept as
 * aliases so the phone shell and older callers keep resolving.
 */
export type ToolId =
  | 'templates' | 'text' | 'image' | 'background' | 'elements' | 'icons' | 'layers' | 'sticker'
  | 'alignment' | 'effects'
  | 'artwork' | 'emoji' | 'shape' | 'arrange' | 'pattern' | 'qr' | 'settings' | 'files';

/** What every part of the designer is handed. Assembled in index.ts. */
export interface DesignerCtx {
  readonly view: DesignerViewSettings;
  setView(patch:Partial<DesignerViewSettings>):void;
  copySelection():void;
  pasteSelection():void;
  share?():Promise<string>;
  readonly selection: Set<string>;
  /** The designer's own root element. */
  readonly root: HTMLElement;
  /** Where overlays and toasts belong (the widget's shadow root, or our root). */
  readonly host: ShadowRoot | HTMLElement;
  readonly rawConfig: DesignAreaConfig;
  readonly config: ResolvedDesignAreaConfig;
  readonly texts: DesignerAllTexts;
  readonly fonts: DesignerFonts;
  readonly artwork: DesignerArtwork;
  readonly uploads: DesignerUploads;
  readonly renderer: DesignerRenderer;
  readonly currency: { format(amount: number): string };
  /** "Moji dizajni" — saved designs, when the host keeps them. */
  readonly library?: import('../designer-types.js').DesignerLibrary;
  /** Alternative print locations of this side ("Na grudima", "Džep"); absent below two. */
  readonly printLocations?: ReadonlyArray<{ id: string; name: string; current: boolean }>;
  /** Moves the design to another print location (the host reopens the designer there). */
  switchPrintLocation?(id: string): void;
  /** The product picture the zone sits on, when the host has one (editor-only). */
  readonly productBackdrop?: import('../designer-types.js').DesignerProductBackdrop;
  readonly store: DesignerStore;
  /** True on a phone-shaped host or a coarse pointer: sheets, not panels. */
  phone(): boolean;
  selected(): DesignLayer | null;
  dispatch(action: DesignerAction): void;
  toast(message: string, kind?: ToastKind): void;
  openTool(tool: ToolId): void;
  closeTool(): void;
  /** Rebuilds the open panel — after the selection or the design changed. */
  refreshPanel(): void;
  /** Redraws one layer on the canvas (a recolour, a filter, new text). */
  invalidate(layerId?: string): void;
  /** "Hide while I work": designer-only, never part of the answer. */
  isHidden(layerId: string): boolean;
  setHidden(layerId: string, hidden: boolean): void;
  /** How many layers this session has added, for the new-layer cascade. */
  addCount(): number;
  problemsFor(layerId: string): DesignProblem[];
  /** The whole design's problems, in order. */
  problems(): DesignProblem[];
  /** The quick start: pick a photo and/or type a name, laid out for you. */
  startFlow?(kind: 'photo' | 'text' | 'photo-text'): void;
  /** A palette colour that reads on the product behind the zone, when the default does not. */
  inkOnProduct?(): Color | undefined;
  /** The product's average colour behind the zone, when its picture is shown. */
  productRgb?(): [number, number, number] | undefined;
}

export interface PanelDef {
  id: ToolId;
  label: string;
  icon: IconName;
  /** Off when the zone forbids this kind; the rail shows it disabled. */
  allowed: boolean;
  /**
   * `false` for a panel that has no rail button — Poravnanje and Efekti open
   * from the contextual toolbar into the same slot, exactly as Zazzle does.
   * Absent means "yes, put it in the rail".
   */
  rail?: boolean;
  /** Fills the panel body. Returns a teardown when it holds resources. */
  render(body: HTMLElement, ctx: DesignerCtx): void | (() => void);
  /** Ran when the rail button is tapped, before the panel renders. */
  onOpen?(ctx: DesignerCtx): void;
}

// ── Icons ────────────────────────────────────────────────────────────────────

/**
 * One line-art set, drawn as stroked paths in a 24-box so every tool button,
 * handle and list row shares a weight. Inline rather than a font: a shop's
 * theme cannot fail to load them, and they inherit `currentColor`.
 */
const PATHS = {
  text: 'M6 5h12M12 5v14M9 19h6',
  image: 'M4 5h16v14H4zM4 16.5l4.6-4.6 3 3L15.2 11l4.8 4.8',
  imageEye: 'M8.6 9.6a1.2 1.2 0 1 1-2.4 0 1.2 1.2 0 0 1 2.4 0z',
  artwork: 'M12 3l2.3 5.6L20 10l-5.7 1.4L12 17l-2.3-5.6L4 10l5.7-1.4z',
  emoji: 'M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18zM9 10h.01M15 10h.01M8.4 14a4.6 4.6 0 0 0 7.2 0',
  shape: 'M4 4h9v9H4zM15.5 10.5a4.8 4.8 0 1 1 0 9.6 4.8 4.8 0 0 1 0-9.6z',
  layers: 'M12 3l8 4.5-8 4.5-8-4.5zM4 12.6l8 4.4 8-4.4M4 16.6l8 4.4 8-4.4',
  undo: 'M4 8h9.5a5 5 0 0 1 0 10H7M4 8l4-4M4 8l4 4',
  redo: 'M20 8h-9.5a5 5 0 0 0 0 10H17M20 8l-4-4M20 8l-4 4',
  close: 'M6 6l12 12M18 6L6 18',
  check: 'M5 13l4 4 10-10',
  plus: 'M12 5v14M5 12h14',
  minus: 'M5 12h14',
  trash: 'M5 7h14M9.5 7V4.5h5V7M7 7l1 13h8l1-13',
  copy: 'M8 8h11v11H8zM5 16V5h11',
  up: 'M12 19.5V5M6 11l6-6 6 6',
  down: 'M12 4.5V19M6 13l6 6 6-6',
  flipH: 'M12 4v16M8.5 8.5L5 12l3.5 3.5M15.5 8.5L19 12l-3.5 3.5',
  flipV: 'M4 12h16M8.5 8.5L12 5l3.5 3.5M8.5 15.5L12 19l3.5-3.5',
  rotate: 'M20 12a8 8 0 1 1-3.2-6.4M20 4.5v5h-5',
  lock: 'M6 11h12v9.5H6zM9 11V8.2a3 3 0 0 1 6 0V11',
  unlock: 'M6 11h12v9.5H6zM9 11V8.2A3 3 0 0 1 14.8 7',
  eye: 'M2.5 12s3.6-6 9.5-6 9.5 6 9.5 6-3.6 6-9.5 6S2.5 12 2.5 12zM12 9.4a2.6 2.6 0 1 0 0 5.2 2.6 2.6 0 0 0 0-5.2z',
  eyeOff: 'M4 4l16 16M10.2 6.3A10.6 10.6 0 0 1 12 6c5.9 0 9.5 6 9.5 6a18 18 0 0 1-3 3.6M6.6 8.6A17 17 0 0 0 2.5 12s3.6 6 9.5 6c1 0 1.9-.1 2.8-.4',
  search: 'M11 4a7 7 0 1 0 0 14 7 7 0 0 0 0-14zM16.2 16.2L20.5 20.5',
  crop: 'M6.5 3v13.5a2 2 0 0 0 2 2H21M3 6.5h13.5a2 2 0 0 1 2 2V21',
  magic: 'M5 19.5L14.5 10M17 3.5l1.2 2.8L21 7.5l-2.8 1.2L17 11.5l-1.2-2.8L13 7.5l2.8-1.2zM8 3.5l.7 1.6 1.6.7-1.6.7L8 8.1l-.7-1.6-1.6-.7 1.6-.7z',
  grip: 'M9 6h.01M9 12h.01M9 18h.01M15 6h.01M15 12h.01M15 18h.01',
  camera: 'M4 8h3.2L8.7 6h6.6L16.8 8H20v11H4zM12 16.6a3.1 3.1 0 1 0 0-6.2 3.1 3.1 0 0 0 0 6.2z',
  upload: 'M12 16.5V4M7 9l5-5 5 5M4 16v4h16v-4',
  alignLeft: 'M4 6h16M4 12h10M4 18h13',
  alignCenter: 'M4 6h16M7 12h10M6 18h12',
  alignRight: 'M4 6h16M10 12h10M7 18h13',
  warning: 'M12 4l9 16H3zM12 10v4M12 17h.01',
  chevron: 'M6 9l6 6 6-6',
  size: 'M4 20L20 4M4 14v6h6M14 4h6v6',
  curve: 'M4 17a14 14 0 0 1 16 0',
  spacing: 'M4 5v14M20 5v14M8 12h8M8 12l2-2M8 12l2 2M16 12l-2-2M16 12l-2 2',
  shadow: 'M9 4h7a4 4 0 0 1 4 4v7M4.5 9.5h10v10h-10z',
  outline: 'M4 4h16v16H4zM8 8h8v8H8z',
  uppercase: 'M3 18L7.5 6l4.5 12M4.6 14h5.8M15 18l3.5-9 3.5 9M15.9 15.4h5.2',
  opacity: 'M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18zM12 3v18',
  // Added with the desktop rebuild: the contextual toolbar, the alignment
  // panel and the bottom bar all need a mark of their own.
  alignTop: 'M4 4h16M6 8h4v12H6zM14 8h4v8h-4z',
  alignMiddle: 'M4 12h16M8 6h3v12H8zM13 8.5h3v7h-3z',
  alignBottom: 'M4 20h16M6 4h4v12H6zM14 8h4v8h-4z',
  alignVLeft: 'M4 4v16M8 6h12v4H8zM8 14h8v4H8z',
  alignVCenter: 'M12 4v16M6 6h12v4H6zM8 14h8v4H8z',
  alignVRight: 'M20 4v16M4 6h12v4H4zM8 14h8v4H8z',
  distributeH: 'M4 4v16M20 4v16M9 8h6v8H9z',
  distributeV: 'M4 4h16M4 20h16M8 9v6h8V9z',
  bold: 'M7 5h6.2a3.5 3.5 0 0 1 0 7H7zM7 12h7a3.5 3.5 0 0 1 0 7H7z',
  italic: 'M10 5h7M7 19h7M14.5 5l-4 14',
  settings: 'M12 9.2a2.8 2.8 0 1 0 0 5.6 2.8 2.8 0 0 0 0-5.6zM19.4 13.6l1.8 1.1-2 3.4-2-.8a7.6 7.6 0 0 1-1.9 1.1l-.3 2.1h-4l-.3-2.1a7.6 7.6 0 0 1-1.9-1.1l-2 .8-2-3.4 1.8-1.1a7.7 7.7 0 0 1 0-3.2L2.8 9.4l2-3.4 2 .8a7.6 7.6 0 0 1 1.9-1.1l.3-2.1h4l.3 2.1c.7.3 1.3.6 1.9 1.1l2-.8 2 3.4-1.8 1.1a7.7 7.7 0 0 1 0 3.2z',
  help: 'M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18zM9.4 9.6a2.7 2.7 0 0 1 5.2.9c0 1.8-2.6 2.2-2.6 4M12 17.2h.01',
  share: 'M12 16V4M8 7.5L12 3.5l4 4M5 14v6h14v-6',
  qr: 'M4 4h6v6H4zM14 4h6v6h-6zM4 14h6v6H4zM14 14h2.5v2.5H14zM17.5 17.5H20V20h-2.5zM14 20h1M19 14h1',
  sticker: 'M13.5 4H6a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h6l8-8v-6a2 2 0 0 0-2-2zM12 20v-6h6',
  template: 'M4 4h16v5H4zM4 11h7v9H4zM13 11h7v9h-7z',
  group: 'M4 4h7v7H4zM13 13h7v7h-7zM11 7h9M7 11v9',
  ungroup: 'M3 3h7v7H3zM14 14h7v7h-7zM12 5l4 4M16 5l-4 4',
  front: 'M6 3h9v9H6zM3.5 9v11.5H15M18 6.5H20.5V18',
  back: 'M9 12h9v9H9zM6 3h12v3M20.5 8V6H18',
  chevronRight: 'M9 6l6 6-6 6',
  chevronLeft: 'M15 6l-6 6 6 6',
  swap: 'M4 8h13l-3-3M20 16H7l3 3',
  phone: 'M8 3h8a1.5 1.5 0 0 1 1.5 1.5v15A1.5 1.5 0 0 1 16 21H8a1.5 1.5 0 0 1-1.5-1.5v-15A1.5 1.5 0 0 1 8 3zM10.5 18h3',
  computer: 'M3 5h18v11H3zM8.5 20h7M12 16v4',
  eyedrop: 'M4.5 19.5l1-3.5 8-8M13.5 8l2.5 2.5M14.5 5.5a2.6 2.6 0 0 1 3.7 3.7L16.5 11 13 7.5z',
  list: 'M5 6h.01M5 12h.01M5 18h.01M9 6h10M9 12h10M9 18h10',
  // A shirt with a print mark on it: the print-location picker.
  location: 'M8 4l-4.5 3 2 3.5L7 9.7V20h10V9.7l1.5.8 2-3.5L16 4a4 4 0 0 1-8 0zM10 13h4v3h-4z',
} as const;

export type IconName = keyof typeof PATHS;

const NO_FILL: ReadonlySet<IconName> = new Set<IconName>(['grip']);

/** The icon as SVG markup; `iconEl` when a node is wanted. */
export function iconSvg(name: IconName, size = 20): string {
  const width = NO_FILL.has(name) ? 2.4 : 1.7;
  const extra = name === 'image' ? `<path d="${PATHS.imageEye}"/>` : '';
  return (
    `<svg viewBox="0 0 24 24" width="${size}" height="${size}" fill="none" stroke="currentColor"` +
    ` stroke-width="${width}" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">` +
    `<path d="${PATHS[name]}"/>${extra}</svg>`
  );
}

export function iconEl(name: IconName, size = 20): HTMLSpanElement {
  const span = document.createElement('span');
  span.className = 'pzd-ico';
  span.innerHTML = iconSvg(name, size);
  return span;
}

// ── The smallest DOM kit that keeps the panels readable ─────────────────────

export function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className?: string,
  text?: string,
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (text !== undefined) node.textContent = text;
  return node;
}

export function iconBtn(
  name: IconName,
  label: string,
  onClick: () => void,
  options: { className?: string; disabled?: boolean; pressed?: boolean; size?: number } = {},
): HTMLButtonElement {
  const button = el('button', options.className ?? 'pzd-icon-btn');
  button.type = 'button';
  button.setAttribute('aria-label', label);
  button.title = label;
  button.innerHTML = iconSvg(name, options.size ?? 20);
  if (options.disabled) button.disabled = true;
  if (options.pressed !== undefined) button.setAttribute('aria-pressed', String(options.pressed));
  button.addEventListener('click', onClick);
  return button;
}

export function textBtn(
  label: string,
  onClick: () => void,
  options: { className?: string; icon?: IconName; disabled?: boolean } = {},
): HTMLButtonElement {
  const button = el('button', options.className ?? 'pzd-ghost');
  button.type = 'button';
  if (options.icon) button.innerHTML = `${iconSvg(options.icon, 16)} `;
  button.appendChild(document.createTextNode(label));
  if (options.disabled) button.disabled = true;
  button.addEventListener('click', onClick);
  return button;
}

/** A labelled block: the head carries the name and an optional live value. */
export function field(label: string, value?: string): { wrap: HTMLDivElement; head: HTMLDivElement; value: HTMLSpanElement } {
  const wrap = el('div', 'pzd-field');
  const head = el('div', 'pzd-field-head');
  const name = el('span', 'pzd-lbl', label);
  const val = el('span', 'pzd-field-val', value ?? '');
  head.append(name, val);
  wrap.appendChild(head);
  return { wrap, head, value: val };
}

export interface ChipItem {
  id: string;
  label?: string;
  icon?: IconName;
  on?: boolean;
  disabled?: boolean;
  ariaLabel?: string;
}

export function chipRow(items: ChipItem[], onPick: (id: string) => void): HTMLDivElement {
  const row = el('div', 'pzd-chips');
  for (const item of items) {
    const chip = el('button', `pzd-chip-btn${item.on ? ' is-on' : ''}`);
    chip.type = 'button';
    if (item.icon) chip.innerHTML = iconSvg(item.icon, 16);
    if (item.label) chip.appendChild(document.createTextNode(item.label));
    chip.setAttribute('aria-label', item.ariaLabel ?? item.label ?? item.id);
    chip.setAttribute('aria-pressed', String(Boolean(item.on)));
    if (item.disabled) chip.disabled = true;
    chip.addEventListener('click', () => onPick(item.id));
    row.appendChild(chip);
  }
  return row;
}

export function segmented(items: ChipItem[], onPick: (id: string) => void): HTMLDivElement {
  const row = el('div', 'pzd-seg');
  row.setAttribute('role', 'group');
  for (const item of items) {
    const button = el('button', item.on ? 'is-on' : '');
    button.type = 'button';
    if (item.icon) button.innerHTML = iconSvg(item.icon, 16);
    if (item.label) button.appendChild(document.createTextNode(item.label));
    button.setAttribute('aria-label', item.ariaLabel ?? item.label ?? item.id);
    button.setAttribute('aria-pressed', String(Boolean(item.on)));
    button.addEventListener('click', () => onPick(item.id));
    row.appendChild(button);
  }
  return row;
}

export function switchRow(label: string, on: boolean, onToggle: (next: boolean) => void): HTMLButtonElement {
  const button = el('button', 'pzd-switch');
  button.type = 'button';
  button.setAttribute('aria-pressed', String(on));
  const text = el('span', undefined, label);
  const knob = el('i');
  button.append(text, knob);
  button.addEventListener('click', () => onToggle(!on));
  return button;
}

export interface SliderOptions {
  label: string;
  min: number;
  max: number;
  step?: number;
  value: number;
  /** How the number reads next to the label ("8 mm", "60 %"). */
  format?(value: number): string;
  /** Every frame of the drag: CSS-only preview. */
  onInput?(value: number): void;
  /** On release: the state change. */
  onCommit(value: number): void;
  /** Snaps to the middle when the drag comes within this much of it. */
  detent?: number;
}

/**
 * A slider that respects the widget's gesture rule: `input` events preview
 * (the caller moves CSS), `change` commits (the caller re-renders). Without
 * that split a bend slider rasterises the text sixty times a second.
 */
export function slider(options: SliderOptions): HTMLDivElement {
  const box = field(options.label, options.format ? options.format(options.value) : String(options.value));
  const input = el('input', 'pzd-range');
  input.type = 'range';
  input.min = String(options.min);
  input.max = String(options.max);
  input.step = String(options.step ?? 1);
  input.value = String(options.value);
  input.setAttribute('aria-label', options.label);
  const read = (): number => {
    const raw = Number(input.value);
    if (!Number.isFinite(raw)) return options.value;
    if (options.detent !== undefined) {
      const middle = (options.min + options.max) / 2;
      if (Math.abs(raw - middle) <= options.detent) return middle;
    }
    return raw;
  };
  input.addEventListener('input', () => {
    const next = read();
    box.value.textContent = options.format ? options.format(next) : String(next);
    options.onInput?.(next);
  });
  input.addEventListener('change', () => options.onCommit(read()));
  box.wrap.appendChild(input);
  return box.wrap;
}

export function hexOf(color: Color | undefined): string {
  return color ? normalizeHex(color.hex) : '';
}

export function sameHex(a: Color | undefined, b: Color | undefined): boolean {
  const x = hexOf(a), y = hexOf(b);
  return Boolean(x) && x === y;
}

export interface SwatchOptions {
  colors: readonly Color[];
  selected?: Color;
  onPick(color: Color): void;
  /** Shows a "no colour" swatch first. */
  none?: { label: string; onPick(): void };
  /** Shows the rainbow swatch that opens the browser's colour picker. */
  custom?: { label: string };
}

export function swatchGrid(options: SwatchOptions): HTMLDivElement {
  const grid = el('div', 'pzd-swatches');
  if (options.none) {
    const button = el('button', `pzd-swatch is-none${options.selected ? '' : ' is-on'}`);
    button.type = 'button';
    button.setAttribute('aria-label', options.none.label);
    button.title = options.none.label;
    button.addEventListener('click', () => options.none?.onPick());
    grid.appendChild(button);
  }
  for (const color of options.colors) {
    const hex = hexOf(color);
    if (!hex) continue;
    const button = el('button', `pzd-swatch${sameHex(color, options.selected) ? ' is-on' : ''}`);
    button.type = 'button';
    button.style.background = hex;
    button.setAttribute('aria-label', hex);
    button.title = hex;
    button.addEventListener('click', () => options.onPick({ hex }));
    grid.appendChild(button);
  }
  if (options.custom) {
    const holder = el('button', 'pzd-swatch is-custom');
    holder.type = 'button';
    holder.setAttribute('aria-label', options.custom.label);
    holder.title = options.custom.label;
    const input = el('input', 'pzd-color-native');
    input.type = 'color';
    input.value = hexOf(options.selected) || '#000000';
    input.setAttribute('aria-label', options.custom.label);
    // The native picker fires `input` per mouse move inside the wheel; the
    // commit is on `change`, so one pick is one undo step.
    input.addEventListener('change', () => {
      const hex = normalizeHex(input.value);
      if (hex) options.onPick({ hex });
    });
    holder.addEventListener('click', () => input.click());
    holder.appendChild(input);
    grid.appendChild(holder);
  }
  return grid;
}

export function tabs(items: ChipItem[], onPick: (id: string) => void): HTMLDivElement {
  const row = el('div', 'pzd-tabs');
  row.setAttribute('role', 'tablist');
  for (const item of items) {
    const button = el('button', item.on ? 'is-on' : '');
    button.type = 'button';
    button.setAttribute('role', 'tab');
    button.setAttribute('aria-selected', String(Boolean(item.on)));
    button.textContent = item.label ?? item.id;
    button.addEventListener('click', () => onPick(item.id));
    row.appendChild(button);
  }
  return row;
}

/**
 * A search box that waits for the typing to stop. `requestAnimationFrame` is
 * no help here (and never fires in a hidden document anyway), so it is a plain
 * timer, cancelled on every keystroke.
 */
export function searchBox(
  placeholder: string,
  value: string,
  onChange: (query: string) => void,
  waitMs = 220,
): HTMLDivElement {
  const box = el('div', 'pzd-search');
  box.innerHTML = iconSvg('search', 16);
  const input = el('input');
  input.type = 'search';
  input.placeholder = placeholder;
  input.value = value;
  input.setAttribute('aria-label', placeholder);
  let timer: number | undefined;
  input.addEventListener('input', () => {
    if (timer !== undefined) window.clearTimeout(timer);
    timer = window.setTimeout(() => onChange(input.value.trim()), waitMs);
  });
  input.addEventListener('keydown', (event) => {
    if ((event as KeyboardEvent).key !== 'Enter') return;
    event.preventDefault();
    if (timer !== undefined) window.clearTimeout(timer);
    onChange(input.value.trim());
  });
  box.appendChild(input);
  return box;
}

export function note(text: string, kind?: 'bad' | 'warn'): HTMLDivElement {
  return el('div', `pzd-note${kind ? ` is-${kind}` : ''}`, text);
}

/**
 * Did this event happen inside `node`?
 *
 * `event.target` is NOT the answer for a listener on the document: the
 * designer lives in the widget's shadow root, and the DOM retargets a shadow
 * event to its HOST before any listener outside the tree sees it. Asking
 * `node.contains(event.target)` therefore answers "no" for every key press and
 * every click the shopper makes inside the designer — which silently killed
 * the whole keyboard and would close a popover the moment it was clicked.
 * `composedPath()` is the shadow-aware question.
 */
export function eventInside(event: Event, node: Node): boolean {
  const path = typeof event.composedPath === 'function' ? event.composedPath() : [];
  if (path.length) return path.includes(node);
  const target = event.target as Node | null;
  return Boolean(target && (node === target || node.contains(target)));
}

/**
 * A collapsible section, the way the reference editor's Effects panel is
 * built: a heading that toggles, the content underneath, the state kept by
 * the caller so a repaint does not close what the shopper opened.
 *
 * Not `<details>`: Safari animates its own disclosure triangle in a way that
 * fights the panel's scroll position, and the heading has to carry a live
 * value ("75 %") on the right.
 */
export function disclosure(
  title: string,
  open: boolean,
  onToggle: (next: boolean) => void,
): { wrap: HTMLDivElement; body: HTMLDivElement; head: HTMLButtonElement } {
  const wrap = el('div', `pzd-disclosure${open ? ' is-open' : ''}`);
  const head = el('button', 'pzd-disclose');
  head.type = 'button';
  head.setAttribute('aria-expanded', String(open));
  head.innerHTML = iconSvg('chevron', 14);
  head.appendChild(document.createTextNode(title));
  head.addEventListener('click', () => onToggle(!open));
  const body = el('div', 'pzd-disclosure-body');
  body.hidden = !open;
  wrap.append(head, body);
  return { wrap, body, head };
}

export function emptyNote(text: string): HTMLDivElement {
  return el('div', 'pzd-hint', text);
}

/**
 * A layer's picture for the Slojevi list: a DOM stand-in rather than a copy of
 * the Skia raster, because one canvas element cannot be in two places and a
 * second rasterisation per row would cost more than the list is worth.
 */
export function layerThumbnail(ctx: DesignerCtx, layer: DesignLayer): HTMLElement {
  const box = el('div', 'pzd-lthumb');
  if (layer.kind === 'shape') {
    box.innerHTML = shapeSvg(layer.shape, {
      size: 28,
      fill: hexOf(layer.fill) || 'none',
      stroke: hexOf(layer.stroke?.color) || undefined,
      strokeWidth: layer.stroke ? 1.5 : 0,
    });
    return box;
  }
  if (layer.kind === 'text') {
    const span = el('span');
    span.style.font = `600 13px/1 ${ctx.fonts.cssFamily(layer.fontId)}, sans-serif`;
    span.style.color = hexOf(layer.color) || '#333';
    span.textContent = (layer.text.trim().split('\n')[0] ?? 'Aa').slice(0, 2) || 'Aa';
    box.appendChild(span);
    return box;
  }
  if(layer.kind==='qr'){box.innerHTML=iconSvg('outline',20);return box;}
  const url = ctx.renderer.assetUrl(layer.assetId);
  if (!url) {
    box.innerHTML = iconSvg(layer.kind === 'image' ? 'image' : 'artwork', 16);
    return box;
  }
  const img = el('img');
  img.src = url;
  img.alt = '';
  img.loading = 'lazy';
  img.style.objectFit = layer.kind === 'image' ? 'cover' : 'contain';
  img.style.width = '100%';
  img.style.height = '100%';
  box.appendChild(img);
  return box;
}

export function layerTitle(ctx: DesignerCtx, layer: DesignLayer): string {
  if(layer.name)return layer.name;
  switch (layer.kind) {
    case 'text': {
      const first = layer.text.split('\n')[0]?.trim() ?? '';
      return first ? first.slice(0, 28) : ctx.texts.layerText;
    }
    case 'image': return ctx.texts.layerImage;
    case 'artwork': return ctx.texts.layerArtwork;
    case 'shape': return ctx.texts.layerShape;
    case 'qr': return 'QR kod';
  }
}

export function iconForKind(kind: DesignLayer['kind']): IconName {
  return kind === 'text' ? 'text' : kind === 'image' ? 'image' : kind === 'artwork' ? 'artwork' : 'shape';
}

// ── The rail and the panel ───────────────────────────────────────────────────

export interface PanelHost {
  /** The tool rail (a bottom bar on a phone). */
  readonly rail: HTMLElement;
  /** The tool panel (a bottom sheet on a phone). */
  readonly panel: HTMLElement;
  open(tool: ToolId): void;
  close(): void;
  current(): ToolId | null;
  /** Rebuilds the open panel in place, keeping the scroll position. */
  refresh(): void;
  /** Re-reads which rail buttons are enabled and which is active. */
  sync(): void;
  destroy(): void;
}

export function createPanels(ctx: DesignerCtx, defs: PanelDef[]): PanelHost {
  const rail = el('div', 'pzd-rail');
  rail.setAttribute('role', 'toolbar');
  rail.setAttribute('aria-label', ctx.texts.title);

  const panel = el('div', 'pzd-panel');
  panel.setAttribute('role', 'region');
  panel.hidden = true;
  const grip = el('div', 'pzd-grip');
  const head = el('div', 'pzd-panel-head');
  const heading = el('h3');
  const body = el('div', 'pzd-panel-body');
  head.append(heading);
  panel.append(grip, head, body);

  let active: ToolId | null = null;
  let teardown: (() => void) | undefined;
  let refreshQueued = false;
  let destroyed = false;
  const focusSelector='button,input,textarea,select,[tabindex]';
  let tabTarget: { index:number; label:string|null; tool:ToolId|null } | undefined;
  body.addEventListener('keydown',event=>{
    if(event.key!=='Tab')return;
    const controls=[...body.querySelectorAll<HTMLElement>(focusSelector)].filter(el=>el.offsetParent!==null&&!el.hasAttribute('disabled'));
    const index=controls.indexOf(event.target as HTMLElement)+(event.shiftKey?-1:1),target=controls[index];
    if(target)tabTarget={index,label:target.getAttribute('aria-label'),tool:active};
    window.setTimeout(()=>{tabTarget=undefined;},0);
  });
  const buttons = new Map<ToolId, HTMLButtonElement>();

  for (const def of defs) {
    // Toolbar-only panels (Poravnanje, Efekti) render into the same slot but
    // have no rail button of their own.
    if (def.rail === false) continue;
    const button = el('button', 'pzd-tool');
    button.type = 'button';
    button.innerHTML = iconSvg(def.icon, 22);
    button.appendChild(el('span', undefined, def.label));
    button.setAttribute('aria-label', def.label);
    button.disabled = !def.allowed;
    button.addEventListener('click', () => {
      if (active === def.id && ctx.phone()) {
        close();
        return;
      }
      open(def.id);
    });
    buttons.set(def.id, button);
    rail.appendChild(button);
  }

  const closeBtn = iconBtn('close', ctx.texts.close, () => close(), { className: 'pzd-icon-btn is-plain' });
  head.appendChild(closeBtn);

  function sync(): void {
    for (const [id, button] of buttons) {
      button.classList.toggle('is-on', active === id);
      button.setAttribute('aria-expanded', String(active === id));
    }
    panel.hidden = active === null;
    ctx.root.querySelector('.pzd-body')?.classList.toggle('no-panel', active === null);
  }

  function paint(): void {
    teardown?.();
    teardown = undefined;
    const def = defs.find((d) => d.id === active);
    body.textContent = '';
    if (!def) return;
    heading.textContent = def.label;
    panel.setAttribute('aria-label', def.label);
    const result = def.render(body, ctx);
    if (typeof result === 'function') teardown = result;
  }

  function open(tool: ToolId): void {
    if(ctx.view.review)ctx.setView({review:false});
    const def = defs.find((d) => d.id === tool);
    if (!def || !def.allowed) {
      if (def) ctx.toast(ctx.texts.toolNotAllowed, 'info');
      return;
    }
    def.onOpen?.(ctx);
    active = tool;
    sync();
    paint();
    body.scrollTop = 0;
  }

  function close(): void {
    if (active === null) return;
    active = null;
    teardown?.();
    teardown = undefined;
    body.textContent = '';
    sync();
  }

  // Drag the sheet's grip down to dismiss it — the gesture a phone shopper
  // tries first, and cheaper than hunting for the X with a thumb.
  let startY: number | undefined;
  grip.addEventListener('pointerdown', (event) => {
    startY = event.clientY;
    try {
      grip.setPointerCapture(event.pointerId);
    } catch {
      /* a mouse without capture support still works through the move events */
    }
  });
  grip.addEventListener('pointermove', (event) => {
    if (startY === undefined) return;
    const dy = event.clientY - startY;
    panel.style.transform = dy > 0 ? `translateY(${Math.round(dy)}px)` : '';
  });
  const endDrag = (event: PointerEvent): void => {
    if (startY === undefined) return;
    const dy = event.clientY - startY;
    startY = undefined;
    panel.style.transform = '';
    if (dy > 60) close();
  };
  grip.addEventListener('pointerup', endDrag);
  grip.addEventListener('pointercancel', () => { startY = undefined; panel.style.transform = ''; });

  sync();

  return {
    rail,
    panel,
    open,
    close,
    current: () => active,
    refresh() {
      if (active === null || refreshQueued) return;
      const requested = active;
      refreshQueued = true;
      // Let a native Tab/blur finish before replacing controls under its focus.
      queueMicrotask(() => {
        refreshQueued = false;
        if (destroyed || active !== requested) return;
        const owner = ctx.root.getRootNode();
        const focused = owner instanceof ShadowRoot ? owner.activeElement : document.activeElement;
        const controls = [...body.querySelectorAll<HTMLElement>(focusSelector)].filter(el=>el.offsetParent!==null&&!el.hasAttribute('disabled'));
        const pending=tabTarget?.tool===requested?tabTarget:undefined;
        const index = pending?.index ?? (focused ? controls.indexOf(focused as HTMLElement) : -1);
        const label = pending?.label ?? (index >= 0 ? focused?.getAttribute('aria-label') : null);
        const top = body.scrollTop;
        paint(); body.scrollTop = top;
        if (index >= 0) {
          const next = [...body.querySelectorAll<HTMLElement>(focusSelector)].filter(el=>el.offsetParent!==null&&!el.hasAttribute('disabled'));
          const target = (label ? next.find(el => el.getAttribute('aria-label') === label) : undefined) ?? next[index];
          window.setTimeout(()=>{if(!destroyed&&active===requested&&target?.isConnected)target.focus({preventScroll:true});},0);
        }
      });
    },
    sync,
    destroy() {
      destroyed = true;
      teardown?.();
      teardown = undefined;
      rail.remove();
      panel.remove();
    },
  };
}
